import React, { useState, useEffect } from "react";
import { Region } from "../../types/enums";
import { useCentralStore } from "../../store/Central";
import { hasFullFeatureSet } from "../../store/Central/selectors";
import { ONBOARDING_STEPS } from "../../constants/onboarding";
import { useAttributesStore } from "../../store/Attributes";
import { StepPersonalInformation } from "./signup-steps/step-personal-information";
import { StepOrganizationInformation } from "./signup-steps/step-organization-information";
import { StepStandardRegion } from "./signup-steps/step-standardregion";
import { StepBankAccount } from "./signup-steps/step-bank-account";
import { StepPriceLists } from "./signup-steps/step-price-lists";
import { StepMigration } from "./signup-steps/step-migration";
//import { StepTeamInvite } from "./signup-steps/step-team-invite";
import { useNavigate, useSearchParams } from "react-router-dom";

export const MultiStepSignupPage: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const showMigrationStepParam = searchParams.has('show_migration_step');
	const {
		profile,
		organization,
		activatedRegions,
		subscriptionTier,
	} = useCentralStore((state) => ({
		profile: state.profile,
		organization: state.organization,
		activatedRegions: state.activatedRegions,
		subscriptionTier: state.subscriptionTier,
	}));
	const { loadAttributes, getOnboardingAttribute } = useAttributesStore((state) => ({
		loadAttributes: state.loadAttributes,
		getOnboardingAttribute: state.getOnboardingAttribute,
	}));

	const [currentStep, setCurrentStep] = useState(1);

	const MIGRATION_CUTOFF = new Date('2025-12-15T00:00:00.000Z');
	const isNewOrganization = organization?.created_at ? new Date(organization.created_at) >= MIGRATION_CUTOFF : false;

	// The showMigrationStepParam tells us that the user is being redirected from the signup flow
	// This information is useful since hasFullFeatureSet is not useful before the organization step is completed
	// Since only after that step the subscription tier is set 
	const showMigrationStep = showMigrationStepParam || (hasFullFeatureSet(subscriptionTier) && isNewOrganization);
	const totalSteps = showMigrationStep ? 6 : 5;


	useEffect(() => {
		if (profile?.id && organization?.id && currentStep === 1) {
			loadAttributes(profile.id, organization.id);

			const orgInfoStatus = getOnboardingAttribute(ONBOARDING_STEPS.ORGANIZATION_INFO);
			const personalInfoStatus = getOnboardingAttribute(ONBOARDING_STEPS.PERSONAL_INFO);
			const regionStatus = getOnboardingAttribute(ONBOARDING_STEPS.REGION);
			const bankAccountStatus = getOnboardingAttribute(ONBOARDING_STEPS.BANK_ACCOUNT);
			const priceListStatus = getOnboardingAttribute(ONBOARDING_STEPS.PRICE_LIST);

			let targetStep = 1;

			if (priceListStatus === 'completed' && showMigrationStep) {
				targetStep = 6;
			} else if (bankAccountStatus === 'completed') {
				targetStep = 5;
			} else if (regionStatus === 'completed') {
				targetStep = 4;
			} else if (personalInfoStatus === 'completed') {
				targetStep = 3;
			} else if (orgInfoStatus === 'completed') {
				targetStep = 2;
			}

			if (currentStep !== targetStep) {
				setCurrentStep(targetStep);
			}
		}
	}, []);



	const handleBack = () => {
		setCurrentStep((prev) => prev - 1);
	};

	const defaultRegion = activatedRegions.find(
		r => r.id === organization?.default_activated_region_id
	);

	return (
		<>
			{currentStep === 1 && (
				<StepOrganizationInformation
					canGoBack={false}
					stepNumber={1}
					totalSteps={totalSteps}
					onComplete={() => setCurrentStep(2)}
				/>
			)}
			{currentStep === 2 && (
				<StepPersonalInformation
					canGoBack={true}
					onGoBack={handleBack}
					stepNumber={2}
					totalSteps={totalSteps}
					onComplete={() => setCurrentStep(3)}
				/>
			)}
			{currentStep === 3 && (
				<StepStandardRegion
					canGoBack={true}
					onGoBack={handleBack}
					stepNumber={3}
					totalSteps={totalSteps}
					onComplete={() => setCurrentStep(4)}
				/>
			)}
			{currentStep === 4 && (
				<StepBankAccount
					canGoBack={true}
					onGoBack={handleBack}
					region={defaultRegion?.region as Region}
					stepNumber={4}
					totalSteps={totalSteps}
					onComplete={() => setCurrentStep(5)}
				/>
			)}
			{currentStep === 5 && (
				<StepPriceLists
					canGoBack={true}
					onGoBack={handleBack}
					region={defaultRegion?.region as Region}
					stepNumber={5}
					totalSteps={totalSteps}
					onComplete={() => showMigrationStep ? setCurrentStep(6) : navigate('/')}
				/>
			)}
			{currentStep === 6 && showMigrationStep && (
				<StepMigration
					canGoBack={true}
					onGoBack={handleBack}
					stepNumber={6}
					totalSteps={totalSteps}
					onComplete={() => navigate('/')} // Only called by migration step if migration decision is NO_MIGRATION, otherwise migration step navigates
				/>
			)}
			{/* {currentStep === 6 && !showMigrationStep && (
				<StepTeamInvite
					canGoBack={true}
					onGoBack={handleBack}
					stepNumber={6}
					totalSteps={totalSteps}
					onComplete={() => {}}
				/>
			)}
			{currentStep === 7 && showMigrationStep && (
				<StepTeamInvite
					canGoBack={true}
					onGoBack={handleBack}
					stepNumber={7}
					totalSteps={totalSteps}
					onComplete={() => {}}
				/>
			)} */}
		</>
	);
};
